import * as React from 'react';
import { TABLE_CONSTANTS, UseInfiniteScrollOptions } from '../types';

// 🎯 Single Responsibility Principle (SRP)
// Este hook SOLO maneja el scroll infinito

interface UseInfiniteScrollParams {
  parentRef: React.RefObject<HTMLDivElement | null>;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  threshold?: number;
  options?: UseInfiniteScrollOptions;
}

export const useInfiniteScroll = ({
  parentRef,
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
  threshold = TABLE_CONSTANTS.DEFAULT_SCROLL_THRESHOLD,
  options = {}
}: UseInfiniteScrollParams): void => {
  const { enabled = true } = options;
  
  // 🎯 Debounce para evitar llamadas excesivas
  const timeoutRef = React.useRef<NodeJS.Timeout | undefined>(undefined);
  const isScrollingRef = React.useRef(false);

  React.useEffect(() => {
    // 🎯 Early returns para mejor performance
    if (!enabled || !fetchNextPage || !hasNextPage || isFetchingNextPage) {
      return;
    }

    const scrollElement = parentRef.current;
    if (!scrollElement) return;

    const handleScroll = () => {
      // 🎯 Evitar múltiples llamadas durante scroll activo
      if (isScrollingRef.current) return;
      
      isScrollingRef.current = true;

      // 🎯 Debounce la verificación
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        const { scrollTop, scrollHeight, clientHeight } = scrollElement;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        
        // 🎯 Verificaciones adicionales de seguridad
        if (
          distanceFromBottom < threshold && 
          hasNextPage && 
          !isFetchingNextPage &&
          fetchNextPage
        ) {
          fetchNextPage();
        }
        
        isScrollingRef.current = false;
      }, 16); // ~60fps
    };

    // 🎯 Opciones del listener para mejor performance
    const scrollOptions: AddEventListenerOptions = {
      passive: true, // No preventDefault needed
      capture: false
    };

    scrollElement.addEventListener('scroll', handleScroll, scrollOptions);
    
    // 🎯 Cleanup completo
    return () => {
      scrollElement.removeEventListener('scroll', handleScroll, scrollOptions);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      isScrollingRef.current = false;
    };
  }, [
    enabled,
    fetchNextPage, 
    hasNextPage, 
    isFetchingNextPage, 
    threshold, 
    parentRef
  ]);
}; 